import classNames from "classnames";
import React, { FC, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";

import {
  SupportEmailPreview,
  useSendSupportTestMutation,
} from "../../slices/SupportSlice";
import {
  errorText,
  isTestAddress,
  rememberTestAddress,
  TEST_ADDRESS_LIMIT,
} from "./support.utils";
import { PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";

/** The last addresses a test went to, in this browser (owner 2026-10-30: "remember my last three choices"). */
export const TEST_ADDRESSES_KEY = "proc_helpdesk_test_to";

const readAddresses = (): string[] => {
  try {
    const list = JSON.parse(localStorage.getItem(TEST_ADDRESSES_KEY) || "[]");
    return Array.isArray(list)
      ? list.filter((x) => typeof x === "string").slice(0, TEST_ADDRESS_LIMIT)
      : [];
  } catch {
    return [];
  }
};

/**
 * "✉️ Send test" on a Help desk case (owner 2026-10-30, Loom 3007fc28): pick the address (the last 3 are one
 * click away), and the email goes out exactly as Send would send it, with "[Test]" in the subject. The customer
 * gets nothing and nothing is saved on the case.
 */
export const SendTestBox: FC<{
  ticketId: string;
  text: string;
  then: "waiting" | "solved";
  signature?: string;
  onClose: () => void;
  /** "New email" (owner 2026-11-03): sends its own test (no case yet); the case's test call otherwise. */
  send?: (to: string) => Promise<SupportEmailPreview>;
  /** Who gets nothing ("The customer" on a case). */
  nobody?: string;
}> = ({
  ticketId,
  text,
  then,
  signature,
  onClose,
  send,
  nobody = "The customer",
}) => {
  const [recent, setRecent] = useState<string[]>(readAddresses);
  const [to, setTo] = useState(recent[0] || "");
  const [sendTest, { isLoading }] = useSendSupportTestMutation();
  const ok = isTestAddress(to);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const doSend = async () => {
    if (!ok || isLoading) return;
    try {
      const sent = send
        ? await send(to.trim())
        : await sendTest({
            id: ticketId,
            text,
            then,
            signature,
            to: to.trim(),
          }).unwrap();
      const next = rememberTestAddress(recent, to);
      setRecent(next);
      try {
        localStorage.setItem(TEST_ADDRESSES_KEY, JSON.stringify(next));
      } catch {
        // Private window: the list just isn't kept.
      }
      toast(`Test sent to ${sent.to}. ${nobody} got nothing.`, {
        type: "success",
      });
      onClose();
    } catch (e) {
      toast(errorText(e, "The test couldn't be sent. Please try again."), {
        type: "error",
      });
    }
  };

  return createPortal(
    <div
      className={
        "fixed inset-0 z-[1000] flex items-start justify-center bg-[#1F2433]/40 px-3 pt-[12vh]"
      }
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      data-testid={"send-test"}>
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={"Send a test"}
        className={
          "w-full max-w-[460px] rounded-[16px] bg-white p-6 shadow-[0_20px_60px_rgba(31,36,51,0.25)]"
        }>
        <div className={"flex items-start justify-between gap-3"}>
          <div>
            <div className={"text-[17px] font-extrabold text-[#1F2433]"}>
              Send a test
            </div>
            <p className={"mt-1 text-[13px] leading-relaxed text-[#5D6475]"}>
              The email exactly as it will go out, with “[Test]” in the subject.{" "}
              {nobody} gets nothing.
            </p>
          </div>
          <button
            type={"button"}
            onClick={onClose}
            aria-label={"Close"}
            className={
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[20px] leading-none text-[#5D6475] hover:bg-[#F4F5F8]"
            }>
            ×
          </button>
        </div>
        <label
          htmlFor={"send-test-to"}
          className={"mt-4 block text-[12.5px] font-bold text-[#3A4256]"}>
          Send it to
        </label>
        <input
          id={"send-test-to"}
          type={"email"}
          autoFocus
          value={to}
          onChange={(e) => setTo(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void doSend()}
          placeholder={"you@example.com"}
          className={
            "mt-1 h-10 w-full rounded-lg border border-[#DCE2EA] px-3 text-[14px] outline-none focus:border-[#6B4EFF]"
          }
        />
        {recent.length > 0 && (
          <div className={"mt-2.5 flex flex-wrap items-center gap-1.5"}>
            <span className={"text-[12px] font-semibold text-[#7A7F8C]"}>
              Recent:
            </span>
            {recent.map((address) => (
              <button
                key={address}
                type={"button"}
                onClick={() => setTo(address)}
                className={classNames(
                  "rounded-full border px-2.5 py-0.5 text-[12.5px] font-semibold",
                  address.toLowerCase() === to.trim().toLowerCase()
                    ? "border-[#6B4EFF] bg-[#F1EDFF] text-[#4B32D6]"
                    : "border-[#DCE2EA] bg-white text-[#3A4256] hover:border-[#B9AEF5]",
                )}>
                {address}
              </button>
            ))}
          </div>
        )}
        <div className={"mt-5 flex justify-end gap-2"}>
          <button
            type={"button"}
            onClick={onClose}
            className={classNames(SMALL_BUTTON, "h-10")}>
            Cancel
          </button>
          <button
            type={"button"}
            disabled={!ok || isLoading}
            title={ok ? undefined : "Enter one email address."}
            onClick={() => void doSend()}
            className={classNames(PRIMARY_BUTTON, "h-10 px-5")}>
            {isLoading ? "Sending…" : "Send test"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
